import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Palette, Plus, Grid3X3, List, Search, Filter,
  Edit2, Store, ShoppingBag, Trash2, Eye, Download,
  Calendar, Tag, Loader2, X, DollarSign, ExternalLink,
  Shirt, Package, Image as ImageIcon
} from 'lucide-react';

// Modal Component
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-border rounded-2xl max-w-lg w-full max-h-[90vh] overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
          <h3 className="font-serif font-bold text-xl text-obsidian">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-obsidian transition-colors p-1 hover:bg-mist rounded">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function MyDesignsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Modal states
  const [editingDesign, setEditingDesign] = useState(null);
  const [editName, setEditName] = useState('');
  const [publishingDesign, setPublishingDesign] = useState(null);
  const [publishTitle, setPublishTitle] = useState('');
  const [publishDescription, setPublishDescription] = useState('');
  const [publishPrice, setPublishPrice] = useState('19.99');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchDesigns();
  }, []);

  const fetchDesigns = async () => {
    try {
      const res = await api.get('/design-library');
      setDesigns(res.data.designs || []);
    } catch (err) {
      toast.error('Failed to load designs');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (design) => {
    setEditingDesign(design);
    setEditName(design.design_name || design.name);
  };

  const handleSaveEdit = async () => {
    if (!editingDesign || !editName.trim()) return;
    
    setActionLoading(true);
    try {
      await api.put(`/designs/${editingDesign.id}`, { name: editName.trim() });
      toast.success('Design updated!');
      setEditingDesign(null);
      fetchDesigns();
    } catch (err) {
      toast.error('Failed to update design');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = (design) => {
    setPublishingDesign(design);
    setPublishTitle((design.design_name || design.name) + ' T-Shirt');
    setPublishDescription(`Custom designed ${design.design_name || design.name} t-shirt. Perfect for expressing your unique style!`);
    setPublishPrice('19.99');
  };

  const handleConfirmPublish = async () => {
    if (!publishingDesign || !publishTitle.trim() || !publishPrice) return;
    
    setActionLoading(true);
    try {
      const res = await api.post(`/designs/${publishingDesign.id}/publish`, {
        design_id: publishingDesign.id,
        title: publishTitle.trim(),
        description: publishDescription.trim(),
        price: parseFloat(publishPrice),
        print_products: ['apparel_tshirt_unisex', 'apparel_hoodie_unisex']
      });
      
      toast.success('Design published to marketplace!');
      setPublishingDesign(null);
      fetchDesigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async (design) => {
    if (!window.confirm('Remove this design from the marketplace?')) return;
    
    try {
      await api.post(`/designs/${design.id}/unpublish`);
      toast.success('Design removed from marketplace');
      fetchDesigns();
    } catch (err) {
      toast.error('Failed to unpublish');
    }
  };

  const handleOrderSample = (design) => {
    // Navigate to design studio with this design loaded for ordering
    navigate(`/design-studio?load=${design.id}&action=order`);
  };

  const handleDelete = async (design) => {
    if (!window.confirm(`Delete "${design.design_name || design.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/designs/${design.id}`);
      toast.success('Design deleted');
      fetchDesigns();
    } catch (err) {
      toast.error('Failed to delete design');
    }
  };

  const handleEditInStudio = (design) => {
    navigate(`/design-studio?load=${design.id}`);
  };

  // Filter designs
  const filteredDesigns = designs.filter(design => {
    const name = (design.design_name || design.name || '').toLowerCase();
    const matchesSearch = name.includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || 
      (filterType === 'published' && design.published) ||
      (filterType === 'drafts' && !design.published) ||
      (filterType === design.product_type);
    return matchesSearch && matchesFilter;
  });

  const productTypes = ['all', 'tshirt', 'hoodie', 'mug', 'poster', 'tote', 'published', 'drafts'];

  return (
    <div className="max-w-7xl mx-auto" data-testid="my-designs-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-serif text-3xl font-bold text-obsidian mb-2">My Creator Library</h1>
            <p className="text-muted-foreground">Manage your saved designs, publish to marketplace, or order samples</p>
          </div>
          <Button 
            onClick={() => navigate('/design-studio')}
            className="bg-gold hover:bg-gold/90 text-obsidian"
            data-testid="create-new-design-btn"
          >
            <Plus size={16} className="mr-2" /> Create New Design
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between bg-mist/50 rounded-xl p-4">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search designs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-designs-input"
            />
          </div>
          
          <div className="flex items-center gap-3">
            {/* Filter Dropdown */}
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-muted-foreground" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="filter-type-select"
              >
                <option value="all">All Designs</option>
                <option value="published">Published</option>
                <option value="drafts">Drafts</option>
                <option value="tshirt">T-Shirts</option>
                <option value="hoodie">Hoodies</option>
                <option value="mug">Mugs</option>
              </select>
            </div>
            
            {/* View Toggle */}
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-obsidian text-white' : 'bg-white text-obsidian hover:bg-mist'}`}
                data-testid="grid-view-btn"
              >
                <Grid3X3 size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-obsidian text-white' : 'bg-white text-obsidian hover:bg-mist'}`}
                data-testid="list-view-btn"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-obsidian">{designs.length}</p>
            <p className="text-sm text-muted-foreground">Total Designs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{designs.filter(d => d.published).length}</p>
            <p className="text-sm text-muted-foreground">Published</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gold">{designs.filter(d => !d.published).length}</p>
            <p className="text-sm text-muted-foreground">Drafts</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{designs.filter(d => d.product_type === 'tshirt').length}</p>
            <p className="text-sm text-muted-foreground">T-Shirts</p>
          </CardContent>
        </Card>
      </div>

      {/* Designs Grid/List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-gold" size={40} />
        </div>
      ) : filteredDesigns.length === 0 ? (
        <div className="text-center py-16 bg-mist/30 rounded-2xl">
          <Palette size={64} className="mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="font-serif text-xl font-semibold text-obsidian mb-2">
            {searchQuery || filterType !== 'all' ? 'No matching designs' : 'No designs yet'}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchQuery || filterType !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Create your first design and save it to your library'}
          </p>
          <Button 
            onClick={() => navigate('/design-studio')}
            className="bg-gold hover:bg-gold/90 text-obsidian"
          >
            <Plus size={16} className="mr-2" /> Create Design
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDesigns.map((design) => (
            <Card 
              key={design.id} 
              className={`group overflow-hidden hover:shadow-lg transition-all ${design.published ? 'ring-2 ring-green-200' : ''}`}
              data-testid={`design-card-${design.id}`}
            >
              <div className="aspect-square bg-mist/50 relative overflow-hidden">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${design.preview_image || design.thumbnail_url}`}
                  alt={design.design_name || design.name}
                  className="w-full h-full object-contain"
                />
                
                {/* Status Badge */}
                {design.published && (
                  <div className="absolute top-3 left-3 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <Store size={12} /> Published
                  </div>
                )}
                
                {/* Product Type Badge */}
                <div className="absolute top-3 right-3 bg-white/90 text-obsidian text-xs px-2 py-1 rounded-full capitalize">
                  {design.product_type || 'tshirt'}
                </div>

                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="sm" variant="secondary" onClick={() => handleEditInStudio(design)} title="Edit in Studio">
                    <Edit2 size={16} />
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => handleOrderSample(design)} title="Order Sample">
                    <ShoppingBag size={16} />
                  </Button>
                </div>
              </div>
              
              <CardContent className="p-4">
                <h3 className="font-semibold text-obsidian truncate mb-1">{design.design_name || design.name}</h3>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <Calendar size={12} />
                  {new Date(design.created_at || design.timestamp).toLocaleDateString()}
                </div>
                
                <div className="flex gap-2">
                  {design.published ? (
                    <>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                        onClick={() => handleUnpublish(design)}
                      >
                        Unpublish
                      </Button>
                      <a 
                        href={`/products/${design.product_id}`}
                        className="flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                      </a>
                    </>
                  ) : (
                    <>
                      <Button 
                        size="sm" 
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handlePublish(design)}
                      >
                        <Store size={14} className="mr-1" /> Publish
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEdit(design)}
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDelete(design)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="space-y-4">
          {filteredDesigns.map((design) => (
            <Card key={design.id} className={`${design.published ? 'ring-2 ring-green-200' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-mist/50 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${design.preview_image || design.thumbnail_url}`}
                      alt={design.design_name || design.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-obsidian truncate">{design.design_name || design.name}</h3>
                      {design.published && (
                        <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">Published</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1 capitalize">
                        <Shirt size={14} /> {design.product_type || 'tshirt'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} /> {new Date(design.created_at || design.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEditInStudio(design)}>
                      <Edit2 size={14} className="mr-1" /> Edit
                    </Button>
                    {design.published ? (
                      <Button size="sm" variant="outline" onClick={() => handleUnpublish(design)}>
                        Unpublish
                      </Button>
                    ) : (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handlePublish(design)}>
                        <Store size={14} className="mr-1" /> Publish
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleOrderSample(design)}>
                      <ShoppingBag size={14} className="mr-1" /> Order
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(design)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Modal isOpen={!!editingDesign} onClose={() => setEditingDesign(null)} title="Edit Design">
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Design Name</Label>
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter design name"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setEditingDesign(null)}>
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-gold hover:bg-gold/90 text-obsidian"
              onClick={handleSaveEdit}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Publish Modal */}
      <Modal isOpen={!!publishingDesign} onClose={() => setPublishingDesign(null)} title="Publish to Marketplace">
        {publishingDesign && (
          <div className="space-y-4">
            <div className="flex gap-4 p-4 bg-mist/50 rounded-lg">
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${publishingDesign.preview_image || publishingDesign.thumbnail_url}`}
                alt={publishingDesign.design_name || publishingDesign.name}
                className="w-20 h-20 object-contain border rounded bg-white"
              />
              <div>
                <p className="font-medium text-obsidian">{publishingDesign.design_name || publishingDesign.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{publishingDesign.product_type || 'T-Shirt'}</p>
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Product Title *</Label>
              <Input 
                value={publishTitle}
                onChange={(e) => setPublishTitle(e.target.value)}
                placeholder="My Custom T-Shirt Design"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Description</Label>
              <Textarea 
                value={publishDescription}
                onChange={(e) => setPublishDescription(e.target.value)}
                placeholder="Describe your design..."
                rows={3}
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Price (USD) *</Label>
              <div className="relative">
                <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input 
                  type="number"
                  min="0.99"
                  step="0.01"
                  value={publishPrice}
                  onChange={(e) => setPublishPrice(e.target.value)}
                  placeholder="19.99"
                  className="pl-9"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setPublishingDesign(null)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleConfirmPublish}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Store size={16} className="mr-2" />}
                Publish & Sell
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
